// `storage` over IndexedDB through Dexie: one database, one table that
// pretends to be a filesystem. The path is the primary key, so listing a
// directory is a prefix query.
import Dexie, { type EntityTable } from 'dexie';
import { entriesUnder, normalizePath, scoped, toText, type Entry, type FileData, type Storage } from '@chess/api-storage';

interface FileRow {
  path: string;
  data: FileData;
  mtime: number;
}

type FilesDb = Dexie & { files: EntityTable<FileRow, 'path'> };

export function openFilesDb(name = 'chess'): FilesDb {
  const db = new Dexie(name) as FilesDb;
  db.version(1).stores({ files: 'path' });
  return db;
}

export class DexieStorage implements Storage {
  private readonly db: FilesDb;

  constructor(db: FilesDb = openFilesDb()) {
    this.db = db;
  }

  async read(path: string): Promise<FileData | undefined> {
    const row = await this.db.files.get(normalizePath(path));
    return row?.data;
  }

  async readText(path: string): Promise<string | undefined> {
    const data = await this.read(path);
    return data === undefined ? undefined : toText(data);
  }

  async write(path: string, data: FileData): Promise<void> {
    const p = normalizePath(path);
    if (!p) throw new Error('cannot write to the root');
    await this.db.files.put({ path: p, data, mtime: Date.now() });
  }

  async exists(path: string): Promise<boolean> {
    return (await this.db.files.where('path').equals(normalizePath(path)).count()) > 0;
  }

  async list(dir: string): Promise<Entry[]> {
    const d = normalizePath(dir);
    const keys = d
      ? await this.db.files.where('path').startsWith(`${d}/`).primaryKeys()
      : await this.db.files.toCollection().primaryKeys();
    return entriesUnder(d, keys);
  }

  async remove(path: string): Promise<void> {
    const p = normalizePath(path);
    if (!p) {
      await this.db.files.clear();
      return;
    }
    await this.db.transaction('rw', this.db.files, async () => {
      await this.db.files.delete(p);
      await this.db.files.where('path').startsWith(`${p}/`).delete();
    });
  }

  scope(dir: string): Storage {
    return scoped(this, dir);
  }
}
